import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { Icon, Text, TextInput } from '@paypalcorp/pp-react';
import parseContent from '../../../lib/parseContent';
import { useScreenSizeInfo } from '../ScreenSizeInfoProvider';

const Search = props => {
  let searcher;
  let FlexSearch;
  const {
    tools = {},
    appSearch: { active, query, results },
    dispatch,
    updateSearchQuery,
    updateSearchResults,
    clearSearch,
    setSearchActive,
    content = {},
    fptiImpression,
    fptiClick
  } = props;

  const toolList = useMemo(() => Object.values(tools), [tools]);
  // Get search results and update store
  const updateSearch = useCallback(
    // Only do search if user hasn't changed the input for 300 ms
    debounce(value => {
      return Promise.resolve(FlexSearch)
        .then(FlexSearch => {
          // Lazy load fuzzy-search
          if (!FlexSearch) {
            return import('flexsearch').then(md => {
              FlexSearch = md.default;
              searcher = new FlexSearch({
                encode: 'balance',
                tokenize: 'full'
              });
              toolList.forEach((tool, i) => {
                searcher.add(i, `${tool.appTitle} ${tool.appBody}`);
              });
              return searcher;
            });
          }
          return searcher;
        })
        .then(searcher => {
          // Do the search and update the store
          const searchResults = searcher
            .search(value)
            .map(index => toolList[index].appKey);
          dispatch(updateSearchQuery(value));
          dispatch(updateSearchResults(searchResults));
          debounceSearchFpti(value, searchResults);
        });
    }, 300),
    [debounce, dispatch]
  );

  const [inputValue, setInputValue] = useState('');

  const onChange = ({ target: { value } }) => {
    setInputValue(value);
    updateSearch(value);
  };
  const onFocus = () => {
    if (active) {
      dispatch(setSearchActive(false));
      doSearchFpti('close');
    } else {
      dispatch(setSearchActive(true));
      doSearchFpti('open');
    }
  };
  const onBlur = () => {
    if (!inputValue) {
      dispatch(setSearchActive(false));
    }
    doSearchFpti('close');
  };
  const clearSearchOnClick = () => {
    setInputValue('');
    dispatch(clearSearch());
  };

  const renderRightIcon = () => {
    if (inputValue) {
      return (
        <Icon
          size="sm"
          aria-label={content.searchTitle}
          name="close"
          as="button"
          onClick={clearSearchOnClick}
        />
      );
    }

    return (
      <Icon
        size="sm"
        name="search"
        aria-label={content.searchTitle}
        as="button"
        onClick={onFocus}
        onBlur={onBlur}
      />
    );
  };

  let dictionary = {
    resultCount: (results && results.length) || 0,
    query
  };

  const getStringifiedResults = results => {
    let stringifiedResults = '';
    if (Array.isArray(results) && results.length) {
      stringifiedResults = results.join(', ');
    }
    return stringifiedResults;
  };

  const doSearchFpti = useCallback(
    (searchAction, value, searchResults) => {
      fptiClick({
        pageLink: 'search',
        fptiLink: `search_${searchAction}`,
        srch: value || query || '',
        result: getStringifiedResults(searchResults ? searchResults : results)
      });
    },
    [fptiClick, query, results]
  );

  const debounceSearchFpti = useCallback(
    debounce(
      (value, searchResults) => doSearchFpti('bar', value, searchResults),
      1000
    ),
    [debounce, query, results]
  );
  useEffect(() => {
    fptiImpression({ pageLink: 'search' });
  }, []);

  const [placeholder, setPlaceholder] = useState(content.searchTitle);
  const { isMobile } = useScreenSizeInfo();
  useEffect(() => {
    if (isMobile) {
      setPlaceholder(null);
      return;
    }
    setPlaceholder(content.searchTitle);
  }, [isMobile]);

  return (
    <div>
      <TextInput
        className={['appcenter-search', active ? 'active' : '']}
        value={inputValue}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-label={content.searchTitle}
        name="search"
        rightIcon={renderRightIcon()}
        autoComplete="off"
      />
      {query ? (
        <Text size="md" medium>
          {results && results.length > 0
            ? parseContent(content.searchResult, dictionary)
            : content.searchBody}
        </Text>
      ) : null}
    </div>
  );
};

Search.propTypes = {
  tools: PropTypes.object.isRequired,
  appSearch: PropTypes.shape({
    active: PropTypes.bool,
    query: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.string)
  }),
  dispatch: PropTypes.func,
  updateSearchQuery: PropTypes.func,
  updateSearchResults: PropTypes.func,
  clearSearch: PropTypes.func.isRequired,
  setSearchActive: PropTypes.func,
  content: PropTypes.shape({
    searchTitle: PropTypes.string.isRequired,
    searchResult: PropTypes.string,
    searchBody: PropTypes.string.isRequired
  }).isRequired,
  fptiImpression: PropTypes.func,
  fptiClick: PropTypes.func
};

export default Search;
